import { useEffect, useState } from 'react';
import { downloadUrls, localDerivative } from './photos.js';
import type { ReplicaDb } from './db.js';

/**
 * Resolves displayable URLs for a set of photos.
 *
 * Two sources, in order of preference. A photo this device captured is still in
 * `photoBlobs`, so it renders from the local blob with no network at all -- which
 * is what makes a just-captured photo appear instantly and keeps working offline.
 * Everything else needs a signed URL, and those are fetched **in one request for
 * the whole set**: a catalog of two hundred items must not mean two hundred round
 * trips.
 *
 * Shared between the catalog list and the photo strip so the batching is done
 * once. Doing it per component is how a grid ends up making a request per
 * thumbnail without anyone noticing.
 */
export interface PhotoRef {
  readonly photoId: string;
  /** Absent for a photo whose metadata has not arrived yet. */
  readonly derivativeKey: string | undefined;
}

export function usePhotoSources(db: ReplicaDb, refs: readonly PhotoRef[]): Record<string, string> {
  const [sources, setSources] = useState<Record<string, string>>({});

  // Keyed on the ids rather than the array, which is rebuilt on every render.
  const signature = refs.map((ref) => ref.photoId).join(',');

  useEffect(() => {
    let live = true;
    const created: string[] = [];

    void (async () => {
      const next: Record<string, string> = {};
      const remote = new Map<string, string>();

      for (const ref of refs) {
        // Stop if the component has gone: the cleanup below revokes whatever was
        // created, and there is nothing left to show it in.
        if (!live) break;

        let blob: Blob | null = null;
        try {
          blob = await localDerivative(db, ref.photoId);
        } catch {
          /*
           * The replica can close under a read in flight -- a sign-out, or a
           * screen left while its thumbnails were loading. Treated as a photo
           * that is not here, which is what it is from this screen's point of
           * view, and which the remote branch below already does for its own
           * failures. Unguarded, it rejected with nothing to catch it.
           */
        }

        if (blob !== null) {
          const url = URL.createObjectURL(blob);
          next[ref.photoId] = url;
          created.push(url);
        } else if (ref.derivativeKey !== undefined) {
          remote.set(ref.derivativeKey, ref.photoId);
        }
      }

      if (remote.size > 0) {
        try {
          const urls = await downloadUrls([...remote.keys()]);
          for (const [key, photoId] of remote) {
            if (urls[key] !== undefined) next[photoId] = urls[key];
          }
        } catch {
          // Offline, or the session ended. Local thumbnails still render, and a
          // missing one is shown as unavailable rather than as an error.
        }
      }

      if (live) setSources(next);
      else for (const url of created) URL.revokeObjectURL(url);
    })();

    return () => {
      live = false;
      // An object URL pins its blob in memory until revoked. Scrolling a catalog
      // of photographed items without this is a steady leak on a phone.
      for (const url of created) URL.revokeObjectURL(url);
    };
    // `refs` is deliberately not a dependency: it is rebuilt on every render, so
    // depending on it would re-fetch the whole set each time. `signature` is the
    // part that actually changes.
  }, [db, signature]);

  return sources;
}
